import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';

interface TradeSpecialtiesSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
}

export function TradeSpecialtiesSelector({ value = [], onChange, required }: TradeSpecialtiesSelectorProps) {
  const { settings } = usePartnerSettings();
  
  const activeSpecialties = settings?.trade_specialties?.filter(s => s.is_active) || [];
  
  const handleToggle = (specialtyId: string) => {
    if (value.includes(specialtyId)) {
      onChange(value.filter(id => id !== specialtyId));
    } else {
      onChange([...value, specialtyId]);
    }
  };

  const handleRemove = (specialtyId: string) => {
    onChange(value.filter(id => id !== specialtyId));
  };

  const getSpecialtyName = (id: string) => {
    return activeSpecialties.find(s => s.id === id)?.name || id;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {value.map(id => (
          <Badge key={id} variant="secondary" className="gap-1">
            {getSpecialtyName(id)}
            <button
              type="button"
              onClick={() => handleRemove(id)}
              className="ml-1 hover:bg-muted rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Trade
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-2">
              <div className="text-sm font-medium">Select Trade Specialties {required && <span className="text-destructive">*</span>}</div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {activeSpecialties.map(specialty => (
                  <div key={specialty.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={specialty.id}
                      checked={value.includes(specialty.id)}
                      onCheckedChange={() => handleToggle(specialty.id)}
                    />
                    <Label
                      htmlFor={specialty.id}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {specialty.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}